import { Component, Input, OnInit, OnDestroy } from '@angular/core';

import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import moment from 'moment';

import { TVEyesArchivedStateEnum } from '../../../../api/mentions/enum/tv-eyes-archived-state.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { TVEyesMentionsService } from '../../services/tveyes-mentions.service';
import { Mention } from '../../models/mention';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { KeywordHighlightModel } from '../../../Misc/models/keyword-highlight.model';
import { MonitorConfigService } from '../../../MonitorShared/services/monitor-config.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { ConfirmDialogModel } from '../../../../Common/Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { MentionsService } from '../../../../Monitor/Core/services/mentions.service';
import { MentionSearchModel } from '../../../../Monitor/Shared/models/mention-search.model';


@Component({
    selector: 'agpr-mention-view-tveyes-panel',
    templateUrl: './mention-view-tveyes-panel.component.html',
    styleUrls: ['./mention-view-tveyes-panel.component.scss'],
    standalone: false
})
export class MentionViewTveyesPanelComponent implements OnInit, OnDestroy {

    @Input() id: string;
    @Input() highlightKeywords: KeywordHighlightModel[];
    @Input() set mention(value: Mention) {
        this._mention = value;
        this.setTVEyesProperties(this._mention);
    }
    get mention() {
        return this._mention;
    }
    @Input() mentionSearch: MentionSearchModel;

    isTvEyes: boolean = false;
    tvEyesThumbnailSrc: string;
    showTvEyesEditButton: boolean = false;
    showArchiveButton: boolean = false;
    hasPrecisionClipPermission: boolean = false;

    private _mention: Mention;
    private subscriptions: Subscription[] = [];

    readonly buttonTypes = ButtonTypesEnum;
    readonly tvEyesState = TVEyesArchivedStateEnum;
    readonly messageSeverityEnum = messageSeverityEnum;

    constructor(
        private coreDialogService: CoreDialogService,
        private tvEyesMentionsService: TVEyesMentionsService,
        private monitorConfigService: MonitorConfigService,
        private currentUserStateService: CurrentUserStateService,
        private mentionService: MentionsService
    ) { }

    ngOnInit() {
        this.subscriptions.push(
            this.monitorConfigService.getTvEyesArchivingEnabled().subscribe(isArchiveEnabled => {
                this.showArchiveButton = isArchiveEnabled && this.mention.tvEyesArchiveState === TVEyesArchivedStateEnum.EDITED;
            }),
            this.currentUserStateService.getPermissions().pipe(
                filter(x => !!x)
            ).subscribe(permissions => {
                this.hasPrecisionClipPermission = permissions?.includes(userPermissionsEnum.precisionClipper);
            })
        );
    }

    openPrecisionClipDialog() {
        const io = {
            inputs: {
                mention: this._mention,
                mentionSearch: this.mentionSearch
            }
        };

        this.coreDialogService.open('PrecisionClipSetupDialogComponent', io);
    }


    openTVEyesDialog() {
        if (!this.mention.url) {
            return;
        }

        const keywords: string[] = this.highlightKeywords
            ?.flatMap(x => x.keywords) ?? [];

        this.tvEyesMentionsService.openTVEyesDialog(this.mention, this.mentionSearch, keywords).subscribe();
    }

    openTvEyesPodcastPlayer() {
        this.tvEyesMentionsService.openTvEyesPodcastPlayer(this.mention.tvEyesPodcastID, this.highlightKeywords);
    }


    openRevertDialog() {
        const io: ConfirmDialogModel = {
            inputs: {
                heading: 'Monitor.revertDialog.header',
                message: 'Monitor.revertDialog.message',
                showCancel: true,
                okLabel: 'Monitor.revertDialog.confirm'
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.mentionService.revertTvEyesToOriginal(this._mention.mentionId).subscribe();
                    }
                }
            }
        };

        this.coreDialogService.open(ConfirmDialogComponent, io);
    }

    openArchiveDialog() {
        const io: ConfirmDialogModel = {
            inputs: {
                heading: 'Mention.details.archiveConfirm.header',
                message: 'Mention.details.archiveConfirm.message',
                showCancel: true,
                okLabel: 'Mention.details.archiveConfirm.header'
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.tvEyesMentionsService.updateTVEyesArchiveStatus({
                            id: this.mention.mentionId,
                            isDirectArchive: true
                        }).subscribe();
                    }
                }
            }
        };

        this.coreDialogService.open(ConfirmDialogComponent, io);
    }


    private setTVEyesProperties(mention: Mention) {
        this.isTvEyes = mention.isTvEyes();
        this.tvEyesThumbnailSrc = null;
        this.showTvEyesEditButton = false;

        if (!this.isTvEyes) {
            return;
        }

        if (mention.tvEyesPodcastID) {
            this.tvEyesThumbnailSrc = mention.thumbnailUrl;
            return;
        }

        const parsedUrl = new URL(mention.url);

        switch (parsedUrl.hostname) {
            case 'mms.tveyes.com':
                if (this.mention.expired) {
                    this.tvEyesThumbnailSrc = null;
                    this.showTvEyesEditButton = false;
                    return;
                }
                this.showTvEyesEditButton = ![TVEyesArchivedStateEnum.PROCESSING, TVEyesArchivedStateEnum.EDITED].includes(mention.tvEyesArchiveState);
                const thumbnailUrl = new URL('https://mms.tveyes.com/library/findThumbnail.asp');
                thumbnailUrl.searchParams.append('StationID', parsedUrl.searchParams.get('StationID'));
                thumbnailUrl.searchParams.append('PartnerID', parsedUrl.searchParams.get('PartnerID'));

                // NOTE: the thumbnail requires a different date format than the playerlette url provides
                const startDate = moment(parsedUrl.searchParams.get('StartDateTime'));
                thumbnailUrl.searchParams.append('DateTime', startDate.format('MM/DD/YYYY HH:mm:ss'));

                this.tvEyesThumbnailSrc = thumbnailUrl.toString();
                break;
            case 'archive.tveyes.com':
                this.showTvEyesEditButton = false;
                parsedUrl.protocol = 'https';
                this.tvEyesThumbnailSrc = parsedUrl.toString().replace(/\.mp[34]$/i, '.jpg');
                break;
            default:
                this.tvEyesThumbnailSrc = null;
                this.showTvEyesEditButton = false;
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
